import { useState, useMemo } from 'react'
import { useCards } from '../lib/cards'
import { useTransactionSummary } from '../lib/transactions'
import { useCategories, type Category } from '../lib/categories'
import EmptyState from '../components/EmptyState'
import Modal from '../components/Modal'
import TransactionForm from '../components/TransactionForm'
import { useToast } from '../components/ToastContainer'

function parseDecimal(str: string | undefined): number {
  if (!str) return 0
  const cleaned = str.replace(/[^\d.-]/g, '')
  const n = parseFloat(cleaned)
  return isNaN(n) ? 0 : n
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value)
}

export default function DashboardPage() {
  const { data: cardsData, isLoading: cardsLoading, error: cardsError } = useCards()
  const { data: catData, isLoading: catLoading, error: catError } = useCategories()
  
  // Use summary endpoint
  const { data: summaryData, isLoading: summaryLoading } = useTransactionSummary()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [txFilter, setTxFilter] = useState<'all' | 'income' | 'expense'>('all')
  const [txSort, setTxSort] = useState<'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc'>('date_desc')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  const { success, error: toastError } = useToast()

  const isLoading = cardsLoading || catLoading || summaryLoading
  const error = cardsError ?? catError

  const cards = cardsData?.cards ?? []
  const categories = catData?.categories ?? []

  // Use transaction summary data
  const totalIncome = summaryData ? parseFloat(summaryData.total_income) : 0
  const totalExpense = summaryData ? parseFloat(summaryData.total_expense) : 0
  const netBalance = totalIncome - totalExpense

  // Process category summary data — MUST be before any early returns
  // to keep React hooks in a consistent order across renders.
  const categorySummary = useMemo(() => {
    if (!summaryData?.by_category) return []
    
    return summaryData.by_category
      .filter(c => {
        if (categoryFilter === 'all') return true
        if (c.type === 'income') {
          return parseFloat(c.income || '0') > 0
        } else {
          return parseFloat(c.expense || '0') > 0
        }
      })
      .sort((a, b) => {
        const aTotal = parseFloat(a.total || '0')
        const bTotal = parseFloat(b.total || '0')
        return bTotal - aTotal
      })
  }, [summaryData, categoryFilter])

  // Separate expense and income categories for the filter dropdown
  const expenseCategories = useMemo(() => {
    return categorySummary.filter(c => c.type === 'expense')
  }, [categorySummary])

  // Calculate values used by both early-return branches and the main render
  const totalExpensesForChart = expenseCategories.reduce((sum, c) => sum + parseFloat(c.expense || '0'), 0)
  const hasNoData = cards.length === 0 && (summaryData?.total_income === '0') && (summaryData?.total_expense === '0') && categories.length === 0

  if (isLoading && !summaryData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-8">
        <div className="inline-flex items-center gap-2 text-[var(--color-danger)]">
          <svg xmlns="http://www.w3.org/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>Erro ao carregar dados</span>
        </div>
      </div>
    )
  }

  // Get category color or default
  const getCategoryColor = (categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId)
    return cat?.color || '#6C757D'
  }

  if (hasNoData) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-8">
        <EmptyState
          icon={
            <svg xmlns="http://www.w3.org/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          }
          title="Seu dashboard está vazio"
          description="Adicione sua primeira transação, cadastre um cartão ou organize suas categorias para começar a gerenciar suas finanças."
          action={{
            label: 'Ver transações',
            onClick: () => window.location.assign('/transactions'),
          }}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Dashboard</h1>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-600)]"
        >
          Nova Transação
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--color-text-muted)]">Saldo líquido</span>
            <span className={`h-2 w-2 rounded-full ${netBalance >= 0 ? 'bg-[var(--color-success)]' : 'bg-[var(--color-danger)]'}`} />
          </div>
          <p className="mt-2 text-xl font-bold text-[var(--color-text)]">{formatCurrency(netBalance)}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--color-text-muted)]">Entradas</span>
            <span className="h-2 w-2 rounded-full bg-[var(--color-success)]" />
          </div>
          <p className="mt-2 text-xl font-bold text-[var(--color-text)]">{formatCurrency(totalIncome)}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--color-text-muted)]">Saídas</span>
            <span className="h-2 w-2 rounded-full bg-[var(--color-danger)]" />
          </div>
          <p className="mt-2 text-xl font-bold text-[var(--color-text)]">{formatCurrency(totalExpense)}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--color-text-muted)]">Cartões</span>
            <span className="h-2 w-2 rounded-full bg-[var(--color-brand-500)]" />
          </div>
          <p className="mt-2 text-xl font-bold text-[var(--color-text)]">{cards.length}</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">Meus cartões</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {cards.length === 0 ? (
              <EmptyState
                icon={
                  <svg xmlns="http://www.w3.org/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0112 4.5zm0 12a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0112 16.5z" />
                    <path fillRule="evenodd" d="M3.75 5.25a.75.75 0 01.75-.75h14.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75h-14.5a.75.75 0 01-.75-.75v-13.5z" clipRule="evenodd" />
                  </svg>
                }
                title="Nenhum cartão cadastrado"
                description="Adicione cartões para rastrear suas compras e gastos."
                action={{
                  label: 'Gerenciar cartões',
                  onClick: () => window.location.assign('/cards'),
                }}
              />
            ) : (
              cards.map((card) => (
                <div key={card.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[var(--color-text)]">{card.name}</span>
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {card.card_type === 'credit' ? 'Crédito' : card.card_type === 'debit' ? 'Débito' : 'Pré-pago'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {card.color ? (
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: card.color }} />
                    ) : (
                      <span className="h-3 w-3 rounded-full bg-[var(--color-brand-200)]" />
                    )}
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {card.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-3">Despesas por Categoria</h2>
          
          {/* Category Filter */}
          <div className="mb-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
            >
              <option value="all">Todas as categorias</option>
              {expenseCategories.map((cat) => (
                <option key={cat.category_id} value={cat.category_id}>
                  {categories.find(c => c.id === cat.category_id)?.name || cat.category_name}
                </option>
              ))}
            </select>
          </div>

          {/* Pie Chart Visualization */}
          <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] p-4">
            {expenseCategories.length === 0 || totalExpensesForChart === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <svg xmlns="http://www.w3.org/svg" className="h-12 w-12 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L5 14.14 2 9.27l5.91-1.01L12 2z" />
                </svg>
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">Nenhuma despesa para visualizar</p>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                {/* Pie Chart */}
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <path
                      d={generatePiePath(expenseCategories, totalExpensesForChart)}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-medium text-[var(--color-text)]">
                      {formatCurrency(totalExpensesForChart)}
                    </span>
                  </div>
                </div>
                
                {/* Legend */}
                <div className="flex-1 space-y-2">
                  {expenseCategories.slice(0, 5).map((cat) => {
                    const color = getCategoryColor(cat.category_id)
                    const amount = parseFloat(cat.expense || '0')
                    const percentage = (amount / totalExpensesForChart) * 100
                    const catName = categories.find(c => c.id === cat.category_id)?.name || cat.category_name
                    return (
                      <div key={cat.category_id} className="flex items-center gap-2 text-xs">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                        <span className="flex-1 text-[var(--color-text)]">{catName}</span>
                        <span className="text-[var(--color-text-muted)]">{percentage.toFixed(1)}%</span>
                      </div>
                    )
                  })}
                  {expenseCategories.length > 5 && (
                    <p className="text-xs text-[var(--color-text-muted)]">+{expenseCategories.length - 5} mais</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-3">Transações recentes</h2>
          
          {/* Filters and Sort */}
          <div className="mb-3 flex flex-wrap gap-2 items-center justify-between">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTxFilter('all')}
                className={`px-3 py-1 text-xs rounded-lg border ${
                  txFilter === 'all'
                    ? 'bg-[var(--color-brand-500)] text-white border-[var(--color-brand-500)]'
                    : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:bg-[var(--color-surface-subtle)]'
                }`}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => setTxFilter('income')}
                className={`px-3 py-1 text-xs rounded-lg border ${
                  txFilter === 'income'
                    ? 'bg-[var(--color-success)] text-white border-[var(--color-success)]'
                    : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:bg-[var(--color-surface-subtle)]'
                }`}
              >
                Entradas
              </button>
              <button
                type="button"
                onClick={() => setTxFilter('expense')}
                className={`px-3 py-1 text-xs rounded-lg border ${
                  txFilter === 'expense'
                    ? 'bg-[var(--color-danger)] text-white border-[var(--color-danger)]'
                    : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:bg-[var(--color-surface-subtle)]'
                }`}
              >
                Saídas
              </button>
            </div>
            <select
              value={txSort}
              onChange={(e) => setTxSort(e.target.value as typeof txSort)}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
            >
              <option value="date_desc">Mais recentes</option>
              <option value="date_asc">Mais antigas</option>
              <option value="amount_desc">Maior valor</option>
              <option value="amount_asc">Menor valor</option>
            </select>
          </div>
        </section>
      </div>

      {/* Transaction Form Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Nova Transação">
        <TransactionForm
          onSuccess={() => {
            setIsModalOpen(false)
            success('Transação criada com sucesso!')
          }}
        />
      </Modal>
    </div>
  )
}

// Helper function to generate SVG pie chart path
function generatePiePath(categories: Array<{ expense: string }>, total: number): string {
  if (total === 0) return ''
  
  let cumulative = 0
  let paths = []
  
  for (let i = 0; i < categories.length; i++) {
    const amount = parseFloat(categories[i].expense || '0')
    if (amount === 0) continue
    
    const startAngle = (cumulative / total) * 360
    const endAngle = ((cumulative + amount) / total) * 360
    
    const startRad = (startAngle - 90) * Math.PI / 180
    const endRad = (endAngle - 90) * Math.PI / 180
    
    const x1 = 50 + 40 * Math.cos(startRad)
    const y1 = 50 + 40 * Math.sin(startRad)
    const x2 = 50 + 40 * Math.cos(endRad)
    const y2 = 50 + 40 * Math.sin(endRad)
    
    const largeArc = endAngle - startAngle > 180 ? 1 : 0
    
    const path = `M50,50 L${x1},${y1} A40,40 0 ${largeArc},1 ${x2},${y2} Z`
    paths.push(path)
    
    cumulative += amount
  }
  
  return paths.join('')
}
